import { useEffect, useState } from 'react';

import type { Task, TaskStatus } from '@nedelnik/shared';

/** Куда встала брошенная задача: статус и ключ порядка. */
export interface Placement {
  status: TaskStatus;
  order_key: string;
}

/**
 * Сколько держать брошенную задачу на новом месте, если правка так и не
 * вернулась в список задач (например, ушла в карантин).
 */
const LANDED_HOLD_MS = 3000;

interface Landed {
  id: string;
  placement: Placement;
}

/**
 * Брошенная задача стоит на новом месте сразу, не дожидаясь, пока правка
 * пройдёт через движок и вернётся в список задач. Без этого между броском и
 * приездом правки задача на миг возвращалась на старое место — скачок назад и
 * обратно (замечание владельца, 2026-09-13). Общее у доски и списка: там и
 * там бросок один и тот же.
 */
export function useLanded(tasks: readonly Task[]): {
  shown: readonly Task[];
  land: (taskId: string, placement: Placement) => void;
} {
  const [landed, setLanded] = useState<Landed | undefined>(undefined);

  const shown =
    landed === undefined
      ? tasks
      : tasks.map((task) => (task.id === landed.id ? { ...task, ...landed.placement } : task));

  // Подмена снимается, как только настоящая задача её догнала. Срок — на
  // случай, когда правка не доехала вовсе: показывать задачу там, где её нет,
  // дольше нескольких секунд нельзя.
  useEffect(() => {
    if (landed === undefined) return undefined;

    const real = tasks.find((task) => task.id === landed.id);
    const caughtUp =
      real === undefined ||
      (real.status === landed.placement.status && real.order_key === landed.placement.order_key);
    if (caughtUp) {
      setLanded(undefined);
      return undefined;
    }

    const timer = setTimeout(() => setLanded(undefined), LANDED_HOLD_MS);
    return () => clearTimeout(timer);
  }, [tasks, landed]);

  return { shown, land: (id, placement) => setLanded({ id, placement }) };
}
